import { Check, Clear } from "@mui/icons-material";
import { Box, Button, MenuItem, useTheme } from "@mui/material";
import { capitalize, debounce } from "lodash";
import { enqueueSnackbar } from "notistack";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { DOCS, FEATURE_FLAGS } from "../utils/constants";

import { get, getFeatureFlag, isAdmin } from "../utils/io";
import { Protected } from "./Authz/Protected";
import RhfTextField from "./form/RhfTextField";
import MainContentContainer from "./Layouts/MainContentContainer";
import StelDataGrid from "./StelDataGrid";
import VirtualHubIndicator from "./VirtualHubIndicator";

function HubList() {
  const history = useHistory();
  const theme = useTheme();

  const defaultSearchInputs = {
    hubId: "",
    hubGroup: "",
    versionStatus: "",
    connected: "",
  };

  const [loading, setLoading] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState(defaultSearchInputs);
  const [searchResults, setSearchResults] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [totalCount, setTotalCount] = useState(0);

  const showVersionStatusFilter =
    isAdmin() || getFeatureFlag(FEATURE_FLAGS.VERSION_STATUS_FILTER);

  const DataGridColumns = [
    {
      field: "id",
      flex: 1,
      headerName: "Hub ID",
      renderCell: (params) => (
        <>
          {params?.value}
          {params?.row?.isVirtual && (
            <Box sx={{ ml: "10px" }}>
              <VirtualHubIndicator />
            </Box>
          )}
        </>
      ),
    },
    {
      field: "connected",
      align: "center",
      flex: 0.7,
      headerAlign: "center",
      headerName: "Connected",
      renderCell: (params) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="100%"
          height="100%"
        >
          {params?.value ? (
            <Check sx={{ color: "green" }} />
          ) : (
            <Clear color="error" />
          )}
        </Box>
      ),
      width: 150,
    },
    {
      field: "groupName",
      align: "center",
      flex: 1,
      headerAlign: "center",
      headerName: "Hub Group",
      renderCell: (params) => params?.row?.hubGroup?.name,
    },
    ...(showVersionStatusFilter
      ? [
          {
            field: "versionStatus",
            align: "center",
            flex: 1,
            headerAlign: "center",
            headerName: "Version Status",
            valueGetter: (value) =>
              value
                ?.split(" ")
                .map((word) => capitalize(word))
                .join(" "),
          },
        ]
      : []),
  ];

  useEffect(() => {
    async function performUpdate(criteria, page, pageSize) {
      setLoading(true);

      const { status, data } = await get("/hubs", {
        hub_id: criteria.hubId,
        group_id: criteria.hubGroup,
        version_status: criteria.versionStatus,
        connected: criteria.connected,
        limit: pageSize,
        offset: pageSize * page,
      });

      if (status !== 200) {
        enqueueSnackbar("Error retrieving Hubs");
        setSearchResults([]);
        setPaginationModel((pre) => ({
          ...pre,
          totalCount: 0,
        }));
        setLoading(false);
        return;
      }

      const newSearchResults = data.hubs.map(
        ({
          connected,
          hub_group: hubGroup = {},
          version_status: versionStatus,
          is_virtual: isVirtual,
          hub_id: hubId,
        }) => ({
          id: hubId,
          connected,
          hubGroup,
          versionStatus,
          isVirtual,
          hubId,
        })
      );

      setSearchResults(newSearchResults);
      setTotalCount(() => data.total_count);

      setLoading(false);
    }

    const debounced = debounce(performUpdate, 300);

    debounced(searchCriteria, paginationModel.page, paginationModel.pageSize);

    // Effect cleanup to cancel previous update calls
    return debounced.cancel;
  }, [searchCriteria, paginationModel.page, paginationModel.pageSize]);

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues: defaultSearchInputs,
  });

  const resetForm = () => {
    reset();
    setSearchCriteria(defaultSearchInputs);
  };

  return (
    <MainContentContainer title="Hubs" docsUrl={DOCS.HUBS.url}>
      <form
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "12px",
        }}
        onSubmit={handleSubmit(setSearchCriteria)}
      >
        <RhfTextField
          name="hubId"
          control={control}
          sx={{
            minWidth: { xs: "100%", sm: 150 },
            marginRight: "15px",
          }}
          label="Hub ID"
          size="small"
          variant="standard"
        />
        <Protected permission="ViewHubGroups">
          <HubGroupSelect control={control} />
        </Protected>
        {showVersionStatusFilter && (
          <RhfTextField
            name="versionStatus"
            control={control}
            select
            sx={{
              minWidth: { xs: "50%", sm: 150 },
              marginRight: "15px",
            }}
            label="Version Status"
            size="small"
            variant="standard"
          >
            <MenuItem value="latest">Latest</MenuItem>
            <MenuItem value="updating">Updating</MenuItem>
            <MenuItem value="requires update">Requires Update</MenuItem>
          </RhfTextField>
        )}
        <RhfTextField
          name="connected"
          control={control}
          select
          sx={{
            minWidth: { xs: "50%", sm: 150 },
            marginRight: "15px",
          }}
          label="Connection"
          size="small"
          variant="standard"
        >
          <MenuItem value>Connected</MenuItem>
          <MenuItem value={false}>Disconnected</MenuItem>
        </RhfTextField>
        <Button
          sx={{
            margin: `${theme.spacing(0, 1)}`,
          }}
          color="primary"
          type="submit"
          variant="contained"
        >
          Search
        </Button>
        <Button
          sx={{
            margin: `${theme.spacing(0, 1)}`,
          }}
          color="error"
          variant="contained"
          onClick={resetForm}
          disabled={!isDirty}
        >
          Clear
        </Button>
      </form>

      <StelDataGrid
        columns={DataGridColumns}
        rows={searchResults}
        loading={loading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[20, 25, 50, 100]}
        rowCount={totalCount}
        paginationMode="server"
        onRowClick={({ row }) => history.push(`/hubs/${row.hubId}`)}
      />
    </MainContentContainer>
  );
}

function HubGroupSelect({ control }) {
  const [hubGroups, setHubGroups] = useState([]);

  useEffect(() => {
    async function getHubGroups() {
      const { data, status } = await get("/hub_groups");
      if (status !== 200) {
        setHubGroups([]);
        enqueueSnackbar("Unable to retrieve hub groups", { variant: "error" });
        return;
      }
      setHubGroups(
        (data?.hub_groups || []).slice().sort((a, b) => {
          const orgCompare = (a.org_name || "").localeCompare(b.org_name || "");
          return orgCompare !== 0 ? orgCompare : a.name.localeCompare(b.name);
        })
      );
    }
    getHubGroups();
  }, []);

  return (
    <RhfTextField
      sx={{
        minWidth: 150,
        marginRight: "15px",
      }}
      name="hubGroup"
      control={control}
      select
      label="Hub Group"
      size="small"
      variant="standard"
    >
      {hubGroups.map(({ id, name, org_name: orgName }) => (
        <MenuItem key={id} value={id}>
          {orgName ? `${orgName} - ${name}` : name}
        </MenuItem>
      ))}
    </RhfTextField>
  );
}

HubGroupSelect.propTypes = {
  control: PropTypes.shape().isRequired,
};

export default HubList;
